<script setup>
import { ref, onMounted, computed } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";

import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";
import { Navigation, Pagination, EffectCoverflow } from "swiper/modules";

const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();

const collections = ref([
	{
		"id": 1,
		"cover": new URL("@/assets/img/stub_1.png", import.meta.url).href,
		"title": "Рис Мистраль пропаренный длиннозерный",
		"weight": "900 г",
		"painting_title": "НА СЕНОКОСЕ",
		"painting_author": "Малевич Казимир",
		"painting_date": "1928-1929",
		"description": "«На сенокосе» – одно из важнейших произведений так называемого второго крестьянского цикла Малевича. Побывав в 1927 году за границей, наблюдая происходящее в европейском искусстве, Малевич мог убедиться в кризисе авангарда – Пикассо с конца 1910-х «повернул к Энгру», потянулась к фигуративности вся парижская школа. Это подталкивало к переменам.\r\nВ 1929 году Малевич готовил ретроспективную персональную выставку в Третьяковской галерее. Но большинство ранних картин осталось в Германии, и в 1928–1929 годах Малевич создал ряд произведений, имитирующих живопись 1900–1910-х, поставив на холстах ложные даты. Так, картина «На сенокосе» была снабжена надписью: «мотив 1909» – с этой датой она и поступила в Третьяковскую галерею.\r\nПрообразом послужила картина «Косарь» (1912, Нижегородский музей), соединявшая стилевые черты иконы и кубизма. Эти отголоски чувствуются и в работе «На сенокосе»: резкая игра светотени, асимметрия в лице и фигуре напоминают о наследии кубизма. Но вместо плоского декоративного фона – широкое пространство пейзажа. Знаменательно появление «природного» зеленого цвета, которого не было в кубизме и супрематизме, Малевич отвергал «зеленый мир мяса и кости». В конце 1920-х он создает свою «социологию цвета»: цвет в полной мере присущ только деревне; яркие одежды соответствуют природному окружению и мироощущению крестьян. Город тяготеет к монохромности, отсюда цветовая сдержанность кубизма – искусства города.\r\nВернулось и прежде отвергаемое «кольцо горизонта», восстанавливаются верх и низ, небо венчает голову человека («<…> наша голова должна касаться звезд»). Эта голова с окладистой бородой уподоблена иконописному образу, а вертикальная ось композиции, проходящая через фигуру косаря, вместе с линией горизонта образует крест. «Крест – крестьяне» – такова, по Малевичу, этимология слова; крестьянский мир – мир христианства, природы, вечных ценностей. В конце жизни художник разочаровался в идее прогресса, футуристическом культе техники. Отсюда ностальгически просветленная нота, звучащая в картине. В персонаже есть нечто непохожее на прежних малевичевских крестьян: и кроткий взгляд этого неуклюжего, непрочно стоящего человека, словно воскресшего из супрематического небытия, и незамутненное красочное сияние, небесная синева.\r\nНеслучайно появление подобного произведения в канун великой трагедии русской деревни. Супрематист Малевич глубоко прозревал смысл реальных исторических событий, не получивших отражения в традиционном искусстве."
	}
]);
const isShowProductDescription = ref({});


const isCoverflow = computed(() => (storeSettings.widthWindow <= 576 ? "coverflow" : ""));
const isCenteredSlides = computed(() => (storeSettings.widthWindow <= 576 ? true : false));

const showProductDescription = (id) => {
	isShowProductDescription.value[id] = !isShowProductDescription.value[id];
};

onMounted(async () => {
	collections.value = await storeRequests.getCollections();
	
});
</script>
<template>
	<section class="collection section" id="collections">
		<div class="container">
			<h2 class="collection__title title2">Коллекция</h2>
			<div class="collection__slider" v-if="collections.length">
				<Swiper
					:modules="[Navigation, Pagination, EffectCoverflow]"
					:observer="true"
					:observeParents="true"
					:observeSlideChildren="true"
					:resizeObserver="true"
					:effect="isCoverflow"
					:centeredSlides="isCenteredSlides"
					:navigation="{
						nextEl: '.button-next',
						prevEl: '.button-prev',
					}"
					:pagination="{
						el: '.pagination',
					}"
					:breakpoints="{
						320: {
							slidesPerView: 1,
							spaceBetween: 20,
							coverflowEffect: {
								rotate: 0,
								stretch: 120,
								depth: 320,
								modifier: 1.2,
								slideShadows: false,
							},
						},
						577: {
							slidesPerView: 2,
							spaceBetween: 20,
						},

						993: {
							slidesPerView: 3,
							spaceBetween: 24,
						},
						1201: {
							slidesPerView: 5,
							spaceBetween: 24,
						},
					}"
					class="collection__swiper"
				>
					<SwiperSlide :class="['collection-slide']" v-for="slide in collections" :key="slide.id">
						<div class="collection-slide__image">
							<img class="full-img" width="164" height="260" :src="slide.cover" :alt="slide.name" />
						</div>
						<div class="collection-slide__content">
							<span class="collection-slide__line"></span>
							<div class="collection-slide__desc">
								<h6 class="collection-slide__name">{{ slide.title }}</h6>
								<span class="collection-slide__weight">{{ slide.weight }}</span>
							</div>
							<button class="collection-slide__btn" @click="showProductDescription(slide.id)">Подробнее</button>
						</div>
					</SwiperSlide>
					<div class="collection__pagination pagination"></div>
				</Swiper>
				<div class="collection__controller controller">
					<div class="collection__button-prev button-prev swiper-btn">
						<svg width="40" height="40"><use href="#prev"></use></svg>
					</div>
					<div class="collection__button-next button-next swiper-btn">
						<svg width="40" height="40"><use href="#next"></use></svg>
					</div>
				</div>
			</div>
		</div>
		<div class="collection__modal">
			<div :class="['collection-modal list-scrollbar', { active: isShowProductDescription[slide.id] }]" v-for="slide in collections" :key="slide.id">
				<button class="collection-modal__close" @click="showProductDescription(slide.id)">
					<svg width="40" height="40"><use href="#x"></use></svg>
				</button>
				<div class="collection-modal__box-image">
					<img class="" width="296" height="470" :src="slide.cover" :alt="slide.name" />
					<div class="collection-modal__info">
						<div class="collection-modal__name">{{ slide.title }}</div>
						<div class="collection-modal__weight">{{ slide.weight }}</div>
					</div>
				</div>
				<div class="collection-modal__content">
					<div class="collection-modal__content-header">
						<h4 class="collection-modal__painting-title">{{ slide.painting_title }}</h4>
						<span class="collection-modal__painting-author">{{ slide.painting_author }}</span>
						<span class="collection-modal__painting-date">{{ slide.painting_date }}</span>
					</div>
					<div class="collection-modal__desc">
						<p>{{ slide.description }}</p>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
@use "@/assets/scss/blocks/swiper";
.collection {
	@media (max-width: vars.$x480) {
		padding-bottom: 3.4rem;
	}
}
.collection__title {
}

.collection__slider {
	position: relative;
	padding: 0 2rem;
	@media (max-width: vars.$l) {
		padding: 0;
	}
	@media (max-width: vars.$xx) {
		margin: 0 calc(var(--padding-container) * -1);
	}
}
.collection__swiper {
}
.collection-slide {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0px;
	height: auto;
	padding: 0 4rem 1.6rem;
	@media (max-width: vars.$xx) {
		opacity: 0.7;
	}
	&.swiper-slide-active {
		opacity: 1;
		.collection-slide__content {
			@media (max-width: vars.$xx) {
				opacity: 1;
			}
		}
	}
}
.collection-slide__image {
	width: 18.6rem;
	@media (max-width: vars.$x480) {
		width: 14.4rem;
	}
	img {
		aspect-ratio: 41 / 65;
	}
}

.collection-slide__content {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-direction: column;
	gap: 2rem;
	transition: opacity var(--transition) ease-in;
	@media (max-width: vars.$xx) {
		opacity: 0;
		gap: 1rem;
	}
}
.collection-slide__line {
	width: 6.1rem;
	height: 1px;
	background-color: rgba(14, 14, 14, 0.24);
}
.collection-slide__desc {
}
.collection-slide__name {
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 140%;
	text-align: center;
	color: #0e0e0e;
	margin-bottom: 0.5rem;
}
.collection-slide__weight {
	display: block;
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 140%;
	text-align: center;
	color: rgba(14, 14, 14, 0.4);
}
.collection-slide__btn {
	display: inline-flex;
	justify-content: center;
	width: fit-content;
	border: 1px solid var(--border-inverse-4);
	border-radius: 20rem;
	padding: 1.9rem 3.9rem;
	font-weight: 500;
	font-size: 2.4rem;
	line-height: 100%;
	color: var(--text-accent-1-100);
	@media (hover: hover) {
		&:hover {
			background: var(--fill-accent-1-1);
			color: #fff;
		}
	}
}
.collection__controller {
	width: 111%;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	align-items: center;
	justify-content: space-between;
	z-index: 1;
	@media (max-width: vars.$l) {
		width: 105%;
	}
	@media (max-width: vars.$m) {
		display: none;
	}
}

.collection__pagination {
	display: none;
	margin-top: 2.4rem;
	@media (max-width: vars.$m) {
		display: flex;
	}
	@media (max-width: vars.$x480) {
		margin-top: 3rem;
	}
}

.collection__modal {
}
.collection-modal {
	position: fixed;
	inset: 0;
	background-color: #fff;
	z-index: 4;
	height: 100%;
	overflow-y: auto;
	display: grid;
	grid-template-columns: 40rem 1fr;
	align-items: start;
	gap: 11rem;
	padding: 12.8rem 15.7rem;
	opacity: 0;
	visibility: hidden;
	transition: opacity var(--transition) ease-in;
	@media (max-width: vars.$lg) {
		gap: 8rem;
		padding: 10rem 10rem;
	}
	@media (max-width: vars.$l) {
		gap: 6rem;
		padding: 5rem;
	}
	@media (max-width: vars.$mn) {
		grid-template-columns: 1fr;
	}
	@media (max-width: vars.$xx) {
		gap: 3rem;
		padding: 3rem 2rem;
	}
	&.active {
		opacity: 1;
		visibility: visible;
	}
}
.collection-modal__close {
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #0c4ca3;
	border-radius: 10rem;
	width: 8.8rem;
	height: 8.8rem;
	top: 6.4rem;
	right: 4rem;
	color: #fff;
	border: 1px solid currentColor;
	@media (max-width: vars.$x480) {
		width: 6rem;
        height: 6rem;
        top: 3rem;
        right: 2rem;
	}
	@media (hover: hover) {
		&:hover {
			color: #0c4ca3;
			background: #fff;
		}
	}
	svg {
		width: 4rem;
		height: 4rem;
	}
}
.collection-modal__box-image {
	img {
		width: 34rem;
		aspect-ratio: 41 / 65;
		@media (max-width: vars.$xx) {
			width: 15rem;
		}
	}
}
.collection-modal__info {
}
.collection-modal__name {
	font-weight: 500;
	font-size: 3.2rem;
	line-height: 120%;
	color: #0c4ca3;
	@media (max-width: vars.$x480) {
		font-size: 2.2rem;
	}
}
.collection-modal__weight {
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 140%;
	color: rgba(14, 14, 14, 0.4);
}
.collection-modal__content {
	padding-top: 3.3rem;
	@media (max-width: vars.$xx) {
		padding-top: 0;
	}
}
.collection-modal__content-header {
	display: grid;
	gap: 0.5rem;
	margin-bottom: 5.7rem;
	@media (max-width: vars.$xx) {
		margin-bottom: 2rem;
	}
}
.collection-modal__painting-title {
	font-weight: 500;
	font-size: 3.2rem;
	line-height: 120%;
	color: #0c4ca3;
	@media (max-width: vars.$x480) {
		font-size: 2.2rem;
	}
}
.collection-modal__painting-author {
	font-weight: 400;
	font-size: 2rem;
	line-height: 140%;
	@media (max-width: vars.$x480) {
		font-size: 1.6rem;
	}
}
.collection-modal__painting-date {
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 140%;
	color: rgba(14, 14, 14, 0.4);
}
.collection-modal__desc {
	font-weight: 400;
	font-size: 2rem;
	line-height: 140%;
	text-align: justify;
	@media (max-width: vars.$x480) {
		font-size: 1.6rem;
		line-height: 120%;
	}
}
</style>
